import { useCallback, useContext } from 'react'
import ModalContext from 'contexts/ModalContext'
import { useAppSelector } from 'hooks/reduxHooks'
import { getCurrentUserId } from 'selectors/accountSelectors'
import ConfirmSpoofSendModal from 'tripPlanner/components/Chat/ConfirmSpoofSendModal'
import { hasConfirmedSpoofSend, markSpoofSendConfirmed } from 'tripPlanner/utils/spoofSendConfirmation'

interface UseSpoofSendGuardArgs {
  isSpoofed: boolean
  hiddenFromCustomer: boolean
  chatId: number | undefined
}

/**
 * Shared guard for USER-initiated Trip Planner sends. Before a spoofed
 * session sends a message into an existing customer-visible chat, prompts a
 * once-per-(session, memberId, chatId) confirmation modal. Everything else
 * (not spoofed, chat hidden from the customer, no chat yet, already
 * confirmed this chat this session) resolves immediately without showing
 * anything.
 */
export function useSpoofSendGuard({ isSpoofed, hiddenFromCustomer, chatId }: UseSpoofSendGuardArgs) {
  const showModal = useContext(ModalContext)
  const memberId = useAppSelector(getCurrentUserId)

  const confirmOrAbort = useCallback(async(): Promise<boolean> => {
    if (!(isSpoofed && !hiddenFromCustomer && chatId !== undefined)) {
      return true
    }

    if (!memberId || hasConfirmedSpoofSend(memberId, chatId)) {
      return true
    }

    const confirmed = await showModal<boolean>(<ConfirmSpoofSendModal />)
    if (confirmed) {
      markSpoofSendConfirmed(memberId, chatId)
    }

    return confirmed
  }, [isSpoofed, hiddenFromCustomer, chatId, memberId, showModal])

  return { confirmOrAbort }
}
